import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getWorkerCode } from '../utils/workerCode';

const StepCard = ({ number, title, children, icon: Icon, status }) => (
  <div className="bg-gray-800/40 border border-gray-700/50 rounded-2xl p-6 relative overflow-hidden transition-all hover:bg-gray-800/60 group">
    <div className="absolute -right-4 -top-4 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
      <SafeIcon icon={Icon} size={100} />
    </div>
    <div className="flex items-start gap-5">
      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center font-black text-white shadow-xl shadow-cyan-900/20 rotate-3 group-hover:rotate-0 transition-transform">
        {number}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-bold text-white tracking-tight">{title}</h4>
          {status && (
            <span className="text-[10px] font-bold px-2 py-1 rounded-md bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 uppercase tracking-widest">
              {status}
            </span>
          )}
        </div>
        <div className="space-y-3 text-gray-400 text-sm leading-relaxed">
          {children}
        </div>
      </div>
    </div>
  </div>
);

export default function DeploymentGuide({ uuid }) {
  const [copied, setCopied] = useState(false);
  const workerCode = getWorkerCode(uuid || 'd342d11e-d424-4583-b36e-524ab1f0afa4');

  const handleCopy = () => {
    navigator.clipboard.writeText(workerCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-10 pb-20">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 text-green-400 border border-green-500/20 text-xs font-bold mb-2">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          READY FOR NEW REPOSITORY
        </div>
        <h2 className="text-4xl font-black text-white tracking-tighter">Connect New Repository</h2>
        <p className="text-gray-500 max-w-xl mx-auto">
          The previous connection has been cleared. Follow these steps to sync this project with your new GitHub destination.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <StepCard number="1" title="Initialize Destination" icon={FiIcons.FiGithub} status="Pending">
          <p>Create a fresh repository on GitHub. Do not include a README or License to keep it clean for the first push.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a 
              href="https://github.com/new" 
              target="_blank" 
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-xl text-xs font-black hover:bg-gray-200 transition-colors"
            >
              <SafeIcon icon={FiIcons.FiPlus} size={14} />
              CREATE NEW REPO
            </a>
          </div>
        </StepCard>

        <StepCard number="2" title="Verify Edge Logic" icon={FiIcons.FiCpu} status="Required">
          <p>The Edge Worker handles the VLESS protocol. Ensure this file is present in your new repo's <code className="text-cyan-400">public/</code> folder.</p>
          <div className="bg-black/40 border border-gray-700/50 rounded-xl p-4 mt-3">
            <div className="flex items-center justify-between mb-3 text-[10px] font-mono text-gray-500">
              <span>PATH: public/_worker.js</span>
              <span className="text-cyan-500/50">VLESS-WS ENGINE</span>
            </div>
            <button 
              onClick={handleCopy}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-xs font-bold transition-all ${copied ? 'bg-green-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600'}`}
            >
              <SafeIcon icon={copied ? FiIcons.FiCheck : FiIcons.FiCopy} size={14} />
              {copied ? 'CODE COPIED TO CLIPBOARD' : 'COPY WORKER SOURCE CODE'}
            </button>
          </div>
        </StepCard>

        <StepCard number="3" title="Cloudflare Pages Link" icon={FiIcons.FiZap} status="Final Step">
          <p>Connect your Cloudflare account to the new GitHub repository. Cloudflare will automatically build and deploy your changes.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="p-3 rounded-xl bg-gray-900/50 border border-gray-800">
              <span className="text-[10px] text-gray-500 block mb-1">BUILD COMMAND</span>
              <span className="text-xs font-mono text-white">npm run build</span>
            </div>
            <div className="p-3 rounded-xl bg-gray-900/50 border border-gray-800">
              <span className="text-[10px] text-gray-500 block mb-1">OUTPUT DIRECTORY</span>
              <span className="text-xs font-mono text-white">dist</span>
            </div>
          </div>
        </StepCard>
      </div>

      <div className="p-6 rounded-2xl bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border border-cyan-500/20 flex gap-5 items-center">
        <div className="hidden md:flex w-12 h-12 bg-cyan-500/20 rounded-full items-center justify-center text-cyan-400">
          <SafeIcon icon={FiIcons.FiInfo} size={24} />
        </div>
        <div className="flex-1">
          <h5 className="text-white font-bold text-sm">Pro Tip: Custom Domains</h5>
          <p className="text-xs text-gray-500 mt-1">
            Once deployed, add a custom domain in Cloudflare Pages settings to bypass <code className="text-gray-400">*.pages.dev</code> restrictions in some regions.
          </p>
        </div>
      </div>
    </div>
  );
}